import LeavePageModal from "@@components/modals/LeavePageModal";
import useApi from "@@hooks/useApi";
import useContent from "@@hooks/useContent";
import useDamageRecognition from "@@pages/item-details/hooks/useDamageRecognitionContext";
import useItemDetailsContext from "@@pages/item-details/hooks/useItemDetailsContext";
import {
  formatEditDamageValues,
  IEditDamageSchema,
} from "@@pages/item-details/schemas/ItemDamageSchema";
import {
  Button,
  Dialog,
  IconButton,
  ITabsItem,
  Tabs,
  Typography,
  useNotification,
} from "@lux/ds";
import { LayoutHerodias, LayoutJay } from "@lux/ds/ContentLayouts";
import { Close } from "@lux/ds/Icon/MaterialIcons";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import CategoryTabs from "./components/category-tabs/CategoryTabs";
import DamageMatrixTab from "./components/category-tabs/DamageMatrixTab/DamageMatrixTab";
import PhotosTab from "./components/category-tabs/PhotosTab/PhotosTab";
import {
  ButtonFooterSide,
  LabelFooterSide,
  StyledActionHerodias,
  StyledContentJay,
  StyledFooterHerodias,
  StyledHeaderJay,
  StyledHerodias,
} from "./DamagesModal.styles";
interface IDamagesModalProps {
  onClose: (resetDamages?: boolean) => void;
}

const DamagesModal: React.FC<IDamagesModalProps> = ({ onClose }) => {
  const api = useApi();

  const {
    content: { itemDetails: commonContent, itemReview: content, qualityCheck },
  } = useContent();
  const { enqueueNotification } = useNotification();
  const { isCategoryBags, itemDetails } = useItemDetailsContext();
  const {
    damageCounts,
    damageThreshold,
    form,
    getItemQualityDetails,
    scoreCount,
  } = useDamageRecognition();

  const [selectedModal, setSelectedModal] = useState<"damageMatrix" | "photos">(
    "damageMatrix",
  );
  const [tab, setTab] = useState<string>("exterior");
  const [showLeavePageModal, setShowLeavePageModal] = useState<boolean>(false);

  const exteriorCount = useMemo(
    () =>
      Object.entries(damageCounts)
        .filter(([key]) =>
          [
            "back",
            "bottom",
            "corners",
            "front",
            "handleOrStrap",
            "leatherOrFabricZipperPull",
            "pocket",
            "side",
            "top",
            "smell",
          ].includes(key),
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .reduce((res, [_, value]) => res + (value as number), 0),
    [damageCounts],
  );

  const interiorCount = useMemo(
    () =>
      Object.entries(damageCounts)
        .filter(([key]) =>
          [
            "lining",
            "interiorPocket",
            "interiorLeatherOrFabricZipperPull",
          ].includes(key),
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .reduce((res, [_, value]) => res + value, 0),
    [damageCounts],
  );

  const hardwareCount = useMemo(
    () =>
      Object.entries(damageCounts)
        .filter(([key]) =>
          [
            "closure",
            "embellishment",
            "key",
            "practicalAttachment",
            "zipper",
          ].includes(key),
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .reduce((res, [_, value]) => res + value, 0),
    [damageCounts],
  );

  const handleChangeTab = useCallback(
    (_: React.SyntheticEvent, value: string) => setTab(value),
    [],
  );

  const tabs = useMemo(
    (): ITabsItem[] => [
      {
        label: content.exterior,
        subLabel: exteriorCount.toString(),
        value: "exterior",
      },
      {
        label: content.interior,
        subLabel: interiorCount.toString(),
        value: "interior",
      },
      {
        label: content.hardware,
        subLabel: hardwareCount.toString(),
        value: "hardware",
      },
    ],
    [content, exteriorCount, interiorCount, hardwareCount],
  );

  const handleBack = useCallback(() => {
    if (form.formState.isDirty) {
      setShowLeavePageModal(true);
    } else {
      onClose();
    }
  }, [form.formState.isDirty, onClose]);

  const handleCancelChanges = useCallback(() => {
    form.reset();
    enqueueNotification(
      { title: commonContent.discardChangesSuccess },
      { variant: "success", autoHideDuration: 4000 },
    );
  }, [commonContent.discardChangesSuccess, enqueueNotification, form]);

  const handleCancelLeave = useCallback(() => setShowLeavePageModal(false), []);

  const handleConfirmLeave = useCallback(() => {
    form.reset();
    onClose();
    enqueueNotification(
      { title: commonContent.discardChangesSuccess },
      { variant: "success", autoHideDuration: 4000 },
    );
  }, [commonContent.discardChangesSuccess, enqueueNotification, form, onClose]);

  const { isPending: isSubmitting, mutate: onSubmitDamages } = useMutation({
    mutationKey: ["SubmitDamages"],
    mutationFn: async () =>
      await api.products_UpdateQualityControl(
        String(itemDetails.id),
        formatEditDamageValues(
          form.getValues() as unknown as IEditDamageSchema,
        ),
      ),
    onSuccess: () => {
      enqueueNotification(
        { title: content.submitDamagesRecognition },
        { variant: "success", autoHideDuration: 4000 },
      );
      getItemQualityDetails();
      onClose();
    },
    onError: () =>
      enqueueNotification(
        { title: content.submitDamagesError },
        { variant: "error", autoHideDuration: 4000 },
      ),
  });

  const handleSubmitDamages = () => {
    onSubmitDamages();
  };

  return (
    <Dialog open fullScreen>
      <StyledHeaderJay>
        <StyledHerodias>
          <Typography variant='h5' weight='bold'>
            {commonContent.damagesRecognition}
          </Typography>
          <StyledActionHerodias>
            <CategoryTabs tab={selectedModal} setTab={setSelectedModal} />
            <IconButton onClick={handleBack}>
              <Close />
            </IconButton>
          </StyledActionHerodias>
        </StyledHerodias>
        {selectedModal === "damageMatrix" && (
          <LayoutJay paddingTop='s' paddingX='l'>
            <Tabs items={tabs} onChange={handleChangeTab} value={tab} />
          </LayoutJay>
        )}
      </StyledHeaderJay>
      <StyledContentJay scroll $padded={selectedModal === "damageMatrix"}>
        {selectedModal === "damageMatrix" ? (
          <DamageMatrixTab activeTab={tab} />
        ) : (
          <PhotosTab />
        )}
      </StyledContentJay>

      <StyledFooterHerodias>
        {isCategoryBags && (
          <LabelFooterSide>
            <LayoutHerodias gap='xxs' sx={{ width: "unset" }}>
              <Typography variant='caption' weight='regular'>
                {commonContent.damageScore}:
              </Typography>
              <Typography
                variant='caption'
                weight='regular'
                sx={{ color: "#847A84" }}
              >
                {scoreCount}
              </Typography>
            </LayoutHerodias>
            <LayoutHerodias gap='xxs' sx={{ width: "unset" }}>
              <Typography variant='caption' weight='regular'>
                {commonContent.suggestedQCResult}:
              </Typography>
              <Typography
                variant='caption'
                weight='regular'
                sx={{ color: "#847A84" }}
              >
                {scoreCount < damageThreshold
                  ? qualityCheck.qualityCheckPass
                  : qualityCheck.qualityCheckFail}
              </Typography>
            </LayoutHerodias>
          </LabelFooterSide>
        )}
        <ButtonFooterSide>
          <Button sx={{ lineHeight: 1 }} onClick={handleCancelChanges}>
            {commonContent.cancelChanges}
          </Button>
          <Button
            loading={isSubmitting}
            onClick={handleSubmitDamages}
            sx={{ lineHeight: 1 }}
            variant='contained'
          >
            {commonContent.submitChangesButton}
          </Button>
        </ButtonFooterSide>
      </StyledFooterHerodias>

      {showLeavePageModal ? (
        <LeavePageModal
          onCancel={handleCancelLeave}
          onConfirm={handleConfirmLeave}
          title={commonContent.leavePage}
          message={commonContent.leavePageDescription}
        />
      ) : null}
    </Dialog>
  );
};

export default DamagesModal;
